import * as z from 'zod';
import { ExternalScheduleFormValues } from './types';
import { isDate } from 'date-fns';
import { schema } from '@forms/helpers';
import { rateTypes } from '@components/schedules/dialogs/external/inputs/concreteDirect/constants';
import { MaterialOption, Schedule } from '@root/globalTypes';
import { getScheduleDialogType } from '@components/rescheduler/dialog/helpers';
import { toInputValue, validateFloat } from '@utils/formHelpers';

const concreteDirectFormSchema = (required?: boolean) => ({
  additionalInformation: schema.input(),
  externalSiteId: schema.input({ name: 'externalSiteId', required }),
  itemExternalId: schema.input(),
  id: z.array(schema.input({ name: 'itemExternalId', required })),
  pourMethod: schema.input({ name: 'pourMethod', required }),
  pourElement: schema.input({ name: 'pourElement', required }),
  isExactQuantity: schema.boolean(),
  slump: schema.input(),
  spacingInMinutes: schema.input(),
  qtyPerHour: schema.input(),
  localStartDatetime: schema.input(),
  quantity: schema.input({ name: 'quantity', required }),
  rateType: z.enum(rateTypes),
  quantityRate: schema.input({ name: 'quantity', required }),
  additive: z
    .object({
      itemExternalId: schema.input(),
      id: z.array(schema.input()),
      quantity: schema.input(),
    })
    .refine(({ itemExternalId, quantity }) => (required && itemExternalId ? quantity : true), {
      message: 'quantity.required',
      path: ['quantity'],
    }),
});

const materialOptionSchema = z
  .object({
    id: z.string(),
    valueType: z.string().optional(),
    includeEstimated: z.boolean().optional(),
    estimatedQuantity: schema.input(),
    finalQuantity: schema.input(),
  })
  .superRefine(({ valueType, estimatedQuantity, finalQuantity }, ctx) => {
    if (valueType !== 'number') return;
    if (estimatedQuantity && !validateFloat(estimatedQuantity))
      ctx.addIssue({
        code: 'custom',
        path: ['estimatedQuantity'],
        message: 'estimated.required.number',
      });
    if (finalQuantity && !validateFloat(finalQuantity))
      ctx.addIssue({ code: 'custom', path: ['finalQuantity'], message: 'final.required.number' });
  });

export const getMaterialOptionsValues = (options: MaterialOption[] = []) =>
  options
    .filter((option) => option.id)
    .map(({ id, valueType, includeEstimated, estimatedQuantity, finalQuantity }) => ({
      id: id as string,
      valueType,
      includeEstimated,
      estimatedQuantity: toInputValue(estimatedQuantity),
      finalQuantity: toInputValue(finalQuantity),
    }));

export const formSchema = (concreteDirect?: boolean, schedule?: Schedule) =>
  z.object({
    date: z.custom<Date>((v) => isDate(v), { message: 'date.required' }).optional(),
    time: z.custom<Date>((v) => isDate(v), { message: 'time.required' }).optional(),
    note: schema.input(),
    extraComment: schema.input(),
    confirmed: schema.boolean(),
    taskId: schema.input(),
    externalContactId: schema.input({
      name: 'contact',
      required:
        getScheduleDialogType(schedule?.task?.scheduleType, schedule?.task?.taskType) ===
        'external',
    }),
    materialOptions: z.array(materialOptionSchema),
    ...concreteDirectFormSchema(concreteDirect),
  });

export const defaultValues: ExternalScheduleFormValues = {
  date: undefined,
  time: undefined,
  note: '',
  extraComment: '',
  confirmed: false,
  taskId: '',
  externalContactId: '',
  externalSiteId: '',
  additionalInformation: '',
  id: [],
  itemExternalId: '',
  pourMethod: '',
  pourElement: '',
  isExactQuantity: false,
  slump: '',
  localStartDatetime: '',
  spacingInMinutes: '',
  quantity: '',
  qtyPerHour: '',
  rateType: 'SPACING_IN_MINUTES',
  quantityRate: '',
  additive: { id: [], itemExternalId: '', quantity: '' },
  materialOptions: [],
};
